import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { CalendarRange } from '../../../../../components/icon/solar-icons.js'
import {
    PortalTimeline,
    TimelineEntry,
} from '../../../../../components/portal-timeline/portal-timeline.js'
import { Section, SectionHeader } from '../../../../../components/section/section.js'
import type {
    EmployeeProfileData,
    EmploymentHistoryRecord,
    RequestConfirmFn,
} from '../employee-profile-types.js'
import '../employee-profile-view.css'
import { toEmploymentTimelineEntries } from '../employment-timeline-entries.js'

interface HistorySectionProps {
    employee: EmployeeProfileData
    employmentHistory: EmploymentHistoryRecord[]
    lang: Language
    onDeleteEmploymentRecord?: (recordId: number) => Promise<boolean>
    requestConfirm: RequestConfirmFn
}

/**
 * "Ametikohtade ja töösuhte ajalugu" — the system-generated chronology of job
 * title / team / location / manager / work-mode changes (TASK-438). Read-only:
 * entries appear automatically from dated employment changes; the only action
 * is undoing a scheduled *future* record before it takes effect.
 */
export const HistorySection = ({
    employee,
    employmentHistory,
    lang,
    onDeleteEmploymentRecord,
    requestConfirm,
}: HistorySectionProps) => {
    const ph = t.portal

    return (
        <Section>
            <SectionHeader icon={CalendarRange} title={ph.profile.employmentHistorySection(lang)} />

            {employmentHistory.length > 0 ? (
                <PortalTimeline
                    entries={toEmploymentTimelineEntries(
                        employmentHistory,
                        employee?.status ?? null,
                        lang,
                    ).map(
                        (entry): TimelineEntry =>
                            entry.isFuture && onDeleteEmploymentRecord
                                ? {
                                      ...entry,
                                      actions: [
                                          {
                                              label: ph.compensation.deleteAction(lang),
                                              onAction: () => {
                                                  requestConfirm(
                                                      ph.compensation.confirmDelete(lang),
                                                      () => void onDeleteEmploymentRecord(entry.id),
                                                      {
                                                          confirmLabel:
                                                              ph.compensation.deleteAction(lang),
                                                          variant: 'danger',
                                                      },
                                                  )
                                              },
                                          },
                                      ],
                                  }
                                : entry,
                    )}
                    emptyMessage={ph.compensation.noEmploymentHistory(lang)}
                    lang={lang}
                />
            ) : (
                <div className="portal-profile__empty-state">
                    {ph.compensation.noEmploymentHistory(lang)}
                </div>
            )}
        </Section>
    )
}
